// Mandanten-Verwaltung: Liste aller registrierten Praxen + Anlegen/Bearbeiten/Entfernen
// inkl. QR-Code-Anzeige (downloadbar, druckbar).
//
// Jede Praxis bekommt beim Anlegen:
//   - eine eindeutige ID
//   - einen QR-Token (im QR-Code, kann später rotiert werden)
//   - Login-Credentials (E-Mail + Passwort) — für späteres Praxis-Admin-Dashboard

import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { BERTSY_LOGO_SVG } from '../lib/bertsy-logo';

// Offizielles Bertsy-Logo als Data-URL für QR-Center.
const BERTSY_LOGO_DATA_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent(BERTSY_LOGO_SVG);

import {
  createPraxisApi,
  deletePraxisApi,
  listPraxen,
  praxisQrPayload,
  updatePraxisApi,
  type CreatePraxisInput,
  type PraxisPublic,
} from '../api';

export default function Praxen() {
  const [praxen, setPraxen] = useState<PraxisPublic[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [editPraxis, setEditPraxis] = useState<PraxisPublic | null>(null);
  const [qrPraxis, setQrPraxis] = useState<PraxisPublic | null>(null);

  const reload = async () => {
    try {
      const r = await listPraxen();
      setPraxen(r.praxen);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async (p: PraxisPublic) => {
    if (!confirm(`Praxis „${p.name}" wirklich entfernen? Patienten, die diesen QR gescannt haben, können dann keine Krankmeldung mehr darüber senden.`)) return;
    try {
      await deletePraxisApi(p.id);
      reload();
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Praxen</h1>
          <p className="page-sub">Registrierte Arztpraxen. Jede hat einen wiederabrufbaren QR-Code + Login-Zugang.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          + Neue Praxis registrieren
        </button>
      </div>

      {err && (
        <div className="card" style={{ color: '#D9534F', marginBottom: 16 }}>
          Fehler: <code>{err}</code>
        </div>
      )}

      <div className="card">
        {!praxen ? (
          <p className="page-sub" style={{ margin: 0 }}>Wird geladen…</p>
        ) : praxen.length === 0 ? (
          <p className="page-sub" style={{ margin: 0 }}>
            Noch keine Praxen registriert. Klick oben rechts auf „Neue Praxis registrieren".
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#5F6B68', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8' }}>Praxis</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8' }}>E-Mail</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 120 }}>Telefon</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 110 }}>Erstellt</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 280 }}></th>
              </tr>
            </thead>
            <tbody>
              {praxen.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #DDE9E8' }}>
                  <td style={{ padding: '12px 8px' }}>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <code style={{ fontSize: 11, color: '#5F6B68' }}>{p.id}</code>
                  </td>
                  <td style={{ padding: '12px 8px' }}>{p.email}</td>
                  <td style={{ padding: '12px 8px', color: '#5F6B68' }}>{p.telefon ?? '—'}</td>
                  <td style={{ padding: '12px 8px', color: '#5F6B68' }}>
                    {new Date(p.created_at).toLocaleDateString('de-DE')}
                  </td>
                  <td style={{ padding: '12px 8px', display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setQrPraxis(p)}>
                      📱 QR
                    </button>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditPraxis(p)}>
                      ✎ Bearbeiten
                    </button>
                    <button className="btn btn-ghost" style={{ color: '#D9534F', fontSize: 12 }} onClick={() => onDelete(p)}>
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <CreateForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
      {editPraxis && <EditForm praxis={editPraxis} onClose={() => setEditPraxis(null)} onSaved={() => { setEditPraxis(null); reload(); }} />}
      {qrPraxis && <QrModal praxis={qrPraxis} onClose={() => setQrPraxis(null)} />}
    </>
  );
}

// ─── Anlegen-Formular ───────────────────────────────────────────────

function CreateForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<CreatePraxisInput>({
    name: '',
    email: '',
    password: '',
    telefon: '',
    adresse: '',
    notiz: '',
  });
  const [password2, setPassword2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = <K extends keyof CreatePraxisInput>(k: K, v: CreatePraxisInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (form.password !== password2) {
      setErr('Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    try {
      await createPraxisApi({
        ...form,
        telefon: form.telefon || undefined,
        adresse: form.adresse || undefined,
        notiz: form.notiz || undefined,
      });
      onSaved();
    } catch (e: any) {
      setErr(translateError(e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Neue Praxis registrieren">
      <form onSubmit={onSubmit}>
        <div className="field">
          <label>Praxis-Name *</label>
          <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus />
        </div>
        <div className="field">
          <label>E-Mail *</label>
          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
        </div>
        <div className="field">
          <label>Passwort (mindestens 8 Zeichen) *</label>
          <input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} minLength={8} required />
        </div>
        <div className="field">
          <label>Passwort wiederholen *</label>
          <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} required />
        </div>
        <div className="field">
          <label>Telefon</label>
          <input type="tel" value={form.telefon} onChange={(e) => set('telefon', e.target.value)} />
        </div>
        <div className="field">
          <label>Adresse</label>
          <input type="text" value={form.adresse} onChange={(e) => set('adresse', e.target.value)} />
        </div>
        <div className="field">
          <label>Interne Notiz (für Bertsy-Admin)</label>
          <textarea rows={2} value={form.notiz} onChange={(e) => set('notiz', e.target.value)} />
        </div>
        {err && <div className="error">{err}</div>}
        <FormButtons loading={loading} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ─── Bearbeiten-Formular ────────────────────────────────────────────

function EditForm({ praxis, onClose, onSaved }: { praxis: PraxisPublic; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(praxis.name);
  const [email, setEmail] = useState(praxis.email);
  const [telefon, setTelefon] = useState(praxis.telefon ?? '');
  const [adresse, setAdresse] = useState(praxis.adresse ?? '');
  const [notiz, setNotiz] = useState(praxis.notiz ?? '');
  const [password, setPassword] = useState('');
  const [rotate, setRotate] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      await updatePraxisApi(praxis.id, {
        name,
        email,
        telefon: telefon || null,
        adresse: adresse || null,
        notiz: notiz || null,
        password: password || undefined,
        rotate_token: rotate || undefined,
      });
      onSaved();
    } catch (e: any) {
      setErr(translateError(e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`„${praxis.name}" bearbeiten`}>
      <form onSubmit={onSubmit}>
        <div className="field">
          <label>Praxis-Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label>E-Mail</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label>Telefon</label>
          <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
        </div>
        <div className="field">
          <label>Adresse</label>
          <input type="text" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
        </div>
        <div className="field">
          <label>Interne Notiz</label>
          <textarea rows={2} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </div>
        <div className="field">
          <label>Neues Passwort (leer = nicht ändern)</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input id="rotate" type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} />
          <label htmlFor="rotate" style={{ margin: 0, textTransform: 'none', letterSpacing: 0, fontWeight: 400, fontSize: 13 }}>
            QR-Token rotieren (alte QR-Codes werden ungültig)
          </label>
        </div>
        {err && <div className="error">{err}</div>}
        <FormButtons loading={loading} onCancel={onClose} okLabel="Speichern" />
      </form>
    </Modal>
  );
}

// ─── QR-Modal ───────────────────────────────────────────────────────

function QrModal({ praxis, onClose }: { praxis: PraxisPublic; onClose: () => void }) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const payload = praxisQrPayload(praxis);
  const [copied, setCopied] = useState<'token' | 'json' | 'snippet' | 'minimal' | null>(null);
  const [tab, setTab] = useState<'qr' | 'widget'>('qr');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');
  const [color, setColor] = useState('#00A099');
  const [label, setLabel] = useState('Mit Bertsy verbinden');

  const onDownload = () => {
    const c = canvasRef.current?.querySelector('canvas');
    if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `bertsy-qr-${praxis.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const onPrint = () => window.print();

  const copy = async (text: string, which: 'token' | 'json') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Fallback: prompt
      prompt('Kopieren:', text);
    }
  };

  const fullSnippet = `<script src="https://cdn.bertsy.de/bertsy-embed.js"
  data-praxis-code="${praxis.qr_token}"
  data-position="${position}"
  data-color="${color}"
  data-label="${label.replace(/"/g, '&quot;')}"
  defer></script>`;
  const minimalSnippet = `<script src="https://cdn.bertsy.de/bertsy-embed.js" data-praxis-code="${praxis.qr_token}" defer></script>`;

  return (
    <Modal onClose={onClose} title={`Verknüpfung: ${praxis.name}`}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #DDE9E8', paddingBottom: 8 }}>
        <button
          className={tab === 'qr' ? 'btn btn-primary' : 'btn btn-ghost'}
          onClick={() => setTab('qr')}
          style={{ flex: 1 }}
        >📱 QR-Code</button>
        <button
          className={tab === 'widget' ? 'btn btn-primary' : 'btn btn-ghost'}
          onClick={() => setTab('widget')}
          style={{ flex: 1 }}
        >🌐 Webseite-Widget</button>
      </div>

      {tab === 'widget' ? (
        <div>
          <p style={{ fontSize: 13, color: '#5F6B68', marginTop: 0 }}>
            Generieren Sie einen Code-Snippet für die Webseite der Praxis. Patienten klicken auf den
            Button und werden direkt in die Bertsy-App geleitet — mit Praxis-Code automatisch verknüpft.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <label>
              <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Position</div>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value as any)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDE9E8', borderRadius: 6, fontSize: 14, background: '#fff' }}
              >
                <option value="bottom-right">Unten rechts</option>
                <option value="bottom-left">Unten links</option>
              </select>
            </label>
            <label>
              <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Button-Farbe</div>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{ width: '100%', height: 38, border: '1px solid #DDE9E8', borderRadius: 6, padding: 2 }}
              />
            </label>
          </div>

          <label style={{ display: 'block', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tooltip-Text</div>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={50}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDE9E8', borderRadius: 6, fontSize: 14 }}
            />
          </label>

          <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            Vollständiger Code-Snippet (zum Einbinden in die Webseite)
          </div>
          <pre style={{
            background: '#1D1D1B', color: '#9FE7E3', padding: 12, borderRadius: 6,
            fontSize: 12, overflow: 'auto', margin: 0, userSelect: 'all',
          }}>{fullSnippet}</pre>
          <button
            className="btn btn-primary"
            style={{ marginTop: 8 }}
            onClick={async () => {
              try { await navigator.clipboard.writeText(fullSnippet); setCopied('snippet'); setTimeout(() => setCopied(null), 1500); }
              catch { prompt('Code:', fullSnippet); }
            }}
          >{copied === 'snippet' ? '✓ Kopiert' : '📋 Vollen Snippet kopieren'}</button>

          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: '#5F6B68' }}>Minimal-Variante (Defaults)</summary>
            <pre style={{
              background: '#1D1D1B', color: '#9FE7E3', padding: 12, borderRadius: 6,
              fontSize: 12, overflow: 'auto', marginTop: 8, userSelect: 'all',
            }}>{minimalSnippet}</pre>
            <button
              className="btn btn-ghost"
              style={{ marginTop: 4, fontSize: 12 }}
              onClick={async () => {
                try { await navigator.clipboard.writeText(minimalSnippet); setCopied('minimal'); setTimeout(() => setCopied(null), 1500); }
                catch { prompt('Code:', minimalSnippet); }
              }}
            >{copied === 'minimal' ? '✓ Kopiert' : '📋 Minimal-Snippet kopieren'}</button>
          </details>

          <div style={{
            marginTop: 16, padding: 12, background: '#F4FAFA',
            borderLeft: '3px solid var(--primary)', borderRadius: 4, fontSize: 12, color: '#1D1D1B',
          }}>
            <strong>So funktioniert es:</strong><br />
            • Patient klickt auf den Bertsy-Button auf der Praxis-Webseite<br />
            • Auf Smartphone: Bertsy-App öffnet sich automatisch mit Praxis-Code (oder leitet zum AppStore)<br />
            • Auf Desktop: QR-Code zum Scannen wird angezeigt<br />
            • Praxis muss nichts weiter tun — Anbindung ist sofort aktiv
          </div>
        </div>
      ) : (
      <div style={{ textAlign: 'center' }}>
        <div ref={canvasRef} style={{ display: 'inline-block', padding: 20, background: '#fff', borderRadius: 8 }}>
          <QRCodeCanvas
            value={payload}
            size={260}
            bgColor="#FFFFFF"
            fgColor="#1D1D1B"
            level="H"
            marginSize={2}
            imageSettings={{
              // Offizielles Bertsy-Logo in der QR-Mitte. Level "H" toleriert die
              // ~20% Verdeckung der QR-Module durch das Logo (Error-Correction).
              src: BERTSY_LOGO_DATA_URL,
              height: 52,
              width: 52,
              excavate: true,
            }}
          />
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#5F6B68' }}>
          <strong>{praxis.name}</strong>
          {praxis.adresse && <><br />{praxis.adresse}</>}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button className="btn btn-primary" onClick={onDownload}>📥 PNG</button>
          <button className="btn btn-ghost" onClick={onPrint}>🖨 Drucken</button>
          <button className="btn btn-ghost" onClick={onClose}>Schließen</button>
        </div>

        {/* Vollständiger Token zum Kopieren — für manuelle Eingabe in der App */}
        <div style={{
          marginTop: 24, padding: 12, background: '#F4FAFA',
          borderRadius: 8, textAlign: 'left',
        }}>
          <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            Praxis-Code (für manuelle Eingabe in der App)
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <code style={{
              flex: 1, padding: '8px 10px', background: '#fff',
              border: '1px solid #DDE9E8', borderRadius: 4,
              fontSize: 12, wordBreak: 'break-all',
            }}>
              {praxis.qr_token}
            </code>
            <button
              className="btn btn-primary"
              style={{ padding: '8px 12px', fontSize: 12 }}
              onClick={() => copy(praxis.qr_token, 'token')}
            >
              {copied === 'token' ? '✓ Kopiert' : 'Kopieren'}
            </button>
          </div>
        </div>

        <p style={{ fontSize: 11, color: '#5F6B68', marginTop: 16, lineHeight: 1.5 }}>
          Patient scannt entweder den QR-Code — oder tippt den Praxis-Code oben manuell in der App
          ein (unter „Praxis-ID manuell eingeben" → „Praxis-Code"). Der Token wird serverseitig
          aufgelöst; die E-Mail-Adresse der Praxis verlässt den Server nicht.
        </p>
      </div>
      )}
    </Modal>
  );
}

// ─── Modal-Wrapper + Buttons ────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,19,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff', padding: 28, borderRadius: 12,
          width: 480, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 16px', color: '#1D1D1B' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function FormButtons({ loading, onCancel, okLabel = 'Anlegen' }: { loading: boolean; onCancel: () => void; okLabel?: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
      <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={loading}>
        Abbrechen
      </button>
      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? 'Speichern…' : okLabel}
      </button>
    </div>
  );
}

function translateError(code: string): string {
  switch (code) {
    case 'email_taken': return 'Diese E-Mail wird schon von einer anderen Praxis verwendet.';
    case 'invalid_email': return 'Bitte eine gültige E-Mail-Adresse eingeben.';
    case 'password_too_short': return 'Passwort muss mindestens 8 Zeichen lang sein.';
    case 'name_too_short': return 'Name muss mindestens 2 Zeichen lang sein.';
    case 'not_found': return 'Praxis nicht gefunden.';
    default: return code;
  }
}
